import React, { useState } from 'react';
import { Box, Button, SvgIcon, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CourseCoverPageContext } from './CourseCoverPageProvider';
import { useContext } from 'react';
import { customSizes, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';
import useAction from '../../../utils/useAction';
import { createCourseRegistration as createCourseRegistrationAction } from '../../../actions/course.actions';
import useRouter from '../../../utils/useRouter';
import { COURSE_REGISTRATION_STATUS, routes } from '../../../constants';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import getMuiIcon, { IconName } from '../../../utils/getMuiIcon';
import { generateCertificatePDF } from '../utils';
import { selectUser } from '../../../reducers/user.selectors';
import { useSelector } from 'react-redux';
import { CourseEnrolled } from '../../../ampli';
import services from '../../../services';
import { getAmpliContentData } from '../../../utils/ampli';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import Dialog from '../../../components/Dialog/Dialog';

const CourseCoverButton: React.FC<{
  coverPageSection: 'summary' | 'certificate';
}> = ({ coverPageSection = 'summary' }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const { router } = useRouter();
  const { isSmallScreen, isMediumScreen } = useScreenSize();
  const { mindItemId, courseMindItem, accessLevel } = useContext(CourseCoverPageContext);
  const createCourseRegistration = useAction(createCourseRegistrationAction);
  const [isLoading, setIsLoading] = useState(false);
  const [isLimitDialogVisible, setLimitDialogVisible] = useState(false);
  const [limitDialogSettings, setLimitDialogSettings] = useState({
    title: '',
    content: '',
    primaryCta: {
      label: '',
      onClick: () => {}
    }
  });

  const handleWithinLimit = () => {
    setLimitDialogVisible(true);
    setLimitDialogSettings({
      title: t('Start a course?'),
      content: t('Your meinUnterricht Pro package allows you one training course per calendar month.'),
      primaryCta: {
        label: t('Start'),
        onClick: () => {
          handleCourseEnroll();
          setLimitDialogVisible(false);
        }
      },
    });
  };

  const redirectToCoursePlayer = () => {
    router.push(`/${routes.coursePlayer.root.replace(':courseId', mindItemId)}`);
  };

  const redirectToCertificatePage = async () => {
    setIsLoading(true);
    try {
      const certificateUrl = await generateCertificatePDF(user, mindItemId);
      window.open(certificateUrl, '_blank');
    } finally {
      setIsLoading(false);
    }
  };

  const trackCourseEnrolled = (mindItem: Partial<MindItem<'course'>>) => {
    const eventProperties = {
      content: getAmpliContentData(mindItem, 'mindItem')
    };

    services.track.ampliEventTrack({ 
      event: new CourseEnrolled(eventProperties)
    });   
  };

  const handleCourseEnroll = async () => {
    setIsLoading(true);
    try {
      await createCourseRegistration(mindItemId);
      trackCourseEnrolled(courseMindItem);
      redirectToCoursePlayer();
    } catch (error) {
      const errorMessage = `Failed to create course enrollment: ${(error as Error).message}; 
      MindItemId: ${mindItemId}
      Button type: ${coverPageSection}`;

      notifyBugsnagHandledError(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonProperties = (): {
    label: string;
    icon?: IconName;
    onClick?: () => void;
  } => {
    const defaultButtonState = {
      label: t('Start course'),
      icon: 'playOutlined' as IconName
    };

    switch (courseMindItem.course?.registrationStatus) {
      case COURSE_REGISTRATION_STATUS.NONE:
        switch (accessLevel) {
          case 'full':
            return {
              label: t('Start course'),
              icon: 'playOutlined',
              onClick: handleCourseEnroll
            }; 

          case 'within-limit':
            return  {
              label: t('Start course'),
              icon: 'playOutlined',
              onClick: handleWithinLimit 
            }; 

          default: 
            return defaultButtonState;
        }

      case COURSE_REGISTRATION_STATUS.PROGRESS:
        return accessLevel === 'none' 
          ? defaultButtonState
          : {
            label: t('Continue course'),
            icon: 'playOutlined',
            onClick: redirectToCoursePlayer
          };
        
      case COURSE_REGISTRATION_STATUS.SUCCESS: {
        if (coverPageSection === 'summary') {
          return accessLevel === 'none' 
            ? defaultButtonState
            : {
              label: t('Go to course'),
              icon: 'playOutlined',
              onClick: redirectToCoursePlayer
            };

        } else {
          return accessLevel === 'none' 
            ? {
              label: t('Start course'),
            }
            : {
              label: t('Go to certificate'),
              onClick: redirectToCertificatePage
            };
        }
      }
      
      default:
        return defaultButtonState;
    }
  };

  const shouldButtonBeDisabled = accessLevel === 'none' || accessLevel === null || isLoading;
  const buttonProps = getButtonProperties();
  return (
    <>
      {coverPageSection === 'summary' && (
        <Button
          sx={{ alignSelf: 'end', paddingX: spaces.lg, flexShrink: 0 }}
          startIcon={buttonProps.icon && <SvgIcon component={getMuiIcon(buttonProps.icon)} />}
          variant='contained'
          size='large'
          color='primary'
          fullWidth={isMediumScreen || isSmallScreen}
          onClick={buttonProps.onClick}
          disabled={shouldButtonBeDisabled}
        >
          {buttonProps.label}
        </Button>
      )}
      {coverPageSection === 'certificate' && (
        <Button
          sx={{ alignSelf: 'start', paddingX: spaces.lg, flexShrink: 0 }}
          variant='outlined'
          color='secondary'
          size='large'
          onClick={buttonProps.onClick}
          disabled={shouldButtonBeDisabled}
        >
          {buttonProps.label}
        </Button>
      )}
      <Dialog
        open={isLimitDialogVisible}
        onClose={() => setLimitDialogVisible(false)}
        title={limitDialogSettings.title}
        htmlBody={(
          <Box sx={{ paddingBottom: spaces.md }}>
            <Typography variant='body1'>
              { limitDialogSettings.content }
            </Typography>
          </Box>
        )}
        primaryCta={{ size: 'medium',...limitDialogSettings.primaryCta }}
        secondaryCta={{ 
          variant: 'text', 
          size: 'medium', 
          label: t('Cancel'), 
          onClick: () => setLimitDialogVisible(false) 
        }}
        desktopWidth={customSizes.coursesLimitDialog.desktopWidth}
      />
    </>
  );
};

export default CourseCoverButton;
